import { Typography } from "@mui/material";

import { isFreePlan, isProPlan } from "@app/analytics/account/Billing/utils";
import ExpandableVideo from "@app/analytics/components/ExpandableVideo";
import IconVideoThumbnail from "@app/analytics/components/IconVideoThumbnail";
import PlayClip from "@app/analytics/components/PlayClip";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useRecordingInfo from "@app/analytics/hooks/recordings/useRecordingInfo";
import type {
  Recommendation as IRecommendation,
  VideoClip,
} from "@app/analytics/types";
import * as RecommendationUtils from "@app/analytics/utils/recommendationUtils";
import useMeasure from "@app/common/hooks/useMeasure";
import { ELLIPSIS_SX } from "@app/common/styles";
import { VideoRecording } from "@app/common/types";

import ExpandableText from "@rui/ExpandableText";
import Stack, { Row } from "@rui/Stack";

import KeyFactors from "./KeyFactors";
import RecommendationActions, {
  RecommendationActionsProps,
} from "./RecommendationActions";
import { Container, Heading, Content, KeyFactor } from "./styles";
import RecommendationActionIcon from "../RecommendationActionIcon";

export interface ConnectedRecommendationProps {
  actionLoading?: RecommendationActionsProps["actionLoading"];
  isSample?: RecommendationActionsProps["isSample"];
  onAccept?: RecommendationActionsProps["onAccept"];
  onReject?: RecommendationActionsProps["onReject"];
  recommendation: IRecommendation;
}

export interface RecommendationProps extends ConnectedRecommendationProps {
  sessionVideoRecording?: VideoRecording;
  videoClip: VideoClip | null;
  showUpsell?: Boolean;
}

type SubcomponentProps = Pick<RecommendationProps, "recommendation">;

const COMPACT_ACTIONS_BREAKPOINT = 500;
const MAX_QUOTE_LENGTH = 280;

const Action = ({ recommendation }: SubcomponentProps) => (
  <Row>
    <Typography sx={{ color: "shades.neutral.800", lineHeight: 20 / 16 }}>
      {RecommendationUtils.getRecommendationDescription(recommendation)}
    </Typography>
  </Row>
);

const Details = ({ recommendation }: SubcomponentProps) => {
  const showKeyFactors = recommendation.kind !== "action_item";
  const reasons = RecommendationUtils.getRecommendationReasons(recommendation);

  return (
    <Row>
      <Stack>
        <Typography variant="labelMed" color="shades.neutral.700">
          More Details
        </Typography>
        {showKeyFactors ? (
          <KeyFactors reasons={reasons} />
        ) : (
          <KeyFactor hideArrow>
            <ExpandableText maxLength={MAX_QUOTE_LENGTH}>
              {RecommendationUtils.getTranscriptText(recommendation)}
            </ExpandableText>
          </KeyFactor>
        )}
      </Stack>
    </Row>
  );
};

const Recommendation = ({
  actionLoading,
  isSample = false,
  onAccept,
  onReject,
  recommendation,
  sessionVideoRecording,
  videoClip,
  showUpsell = false,
}: RecommendationProps) => {
  const recommendationIsReviewed = recommendation.completedOn != null;
  const [containerRef, , { innerWidth }] = useMeasure();

  const compactActions = innerWidth < COMPACT_ACTIONS_BREAKPOINT;

  return (
    <Container ref={containerRef}>
      <Heading variant="apart" spacing={1}>
        <Row spacing={2} variant="left" sx={{ overflow: "hidden" }}>
          <RecommendationActionIcon recommendation={recommendation} />
          <Typography
            variant="bodyPrimaryMed"
            sx={ELLIPSIS_SX}
            title={recommendation.label}
          >
            {recommendation.label}
          </Typography>
        </Row>
        {recommendationIsReviewed ? (
          <Row spacing={2} sx={{ maxWidth: "50%" }}>
            <Typography
              sx={{ color: "shades.neutral.800", ...ELLIPSIS_SX }}
              title={recommendation.meetingContext.title}
            >
              {recommendation.meetingContext.title}
            </Typography>
            {/* TODO: ENG 8466 Add support for viewing all meetings in this series */}
          </Row>
        ) : (
          <RecommendationActions
            actionLoading={actionLoading}
            compact={compactActions}
            isSample={isSample}
            onAccept={onAccept}
            onReject={onReject}
            recommendation={recommendation}
          />
        )}
      </Heading>
      <Content spacing={2} variant="apart" alignItems="flex-start">
        <Stack spacing={2}>
          <Action recommendation={recommendation} />
          <Details recommendation={recommendation} />
        </Stack>
        {videoClip && sessionVideoRecording && (
          <ExpandableVideo
            containerSx={{ flexShrink: 0 }}
            clip={videoClip}
            forcePauseOnMinimize
            maximizeOnPlay="always"
            recordingInfo={sessionVideoRecording}
            renderMinimizedOverlay={(props) =>
              showUpsell ? (
                <IconVideoThumbnail
                  sx={{
                    flexShrink: 0,
                    backgroundColor: "rgba(0,0,0,0.1)",
                    position: "absolute",
                  }}
                  state="locked"
                />
              ) : (
                <PlayClip
                  sx={{ position: "absolute", bottom: "3px", right: "9px" }}
                  {...props}
                />
              )
            }
            useHlsDuration={RecommendationUtils.isCoachingRecommendation(
              recommendation
            )}
          />
        )}
      </Content>
    </Container>
  );
};

const ConnectedRecommendation = (props: ConnectedRecommendationProps) => {
  const { recommendation } = props;

  const { currentPlan } = useBillingContext();

  const sessionId = RecommendationUtils.getRecordingSessionId(recommendation);
  const showUpsell =
    isFreePlan(currentPlan.type) || isProPlan(currentPlan.type);

  // for each unique session id, get recording info then combine clips with the recording info
  const { recordingInfo } = useRecordingInfo(sessionId, {
    enabled: RecommendationUtils.getRecordingInfoEnabled(recommendation),
  });

  const sessionVideoRecording = RecommendationUtils.getSessionVideoRecording(
    recommendation,
    recordingInfo
  );

  const videoClip = RecommendationUtils.getVideoClip(
    recommendation,
    sessionVideoRecording
  );

  return (
    <Recommendation
      {...props}
      sessionVideoRecording={sessionVideoRecording}
      videoClip={videoClip}
      showUpsell={showUpsell}
    />
  );
};

export { Recommendation };
export default ConnectedRecommendation;
